import { useState, useRef, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';

export default function Contacto() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('company_alt') as string)?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    const data: Record<string, string> = {};
    formData.forEach((value, key) => {
      if (key !== 'company_alt') {
        data[key] = value as string;
      }
    });

    setFormStatus('submitting');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/da2896tv7gurs51ef8tg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = serverMsg?.toLowerCase().includes('spam') || serverMsg?.toLowerCase().includes('form data is spam');

      if (!response.ok || (parsed && parsed.code !== 'OK') || isSpam) {
        setFormStatus('error');
        setFormError(isSpam ? 'Ha ocurrido un error. Por favor intenta de nuevo.' : (serverMsg || 'Error al enviar el formulario'));
        return;
      }

      setFormStatus('success');
      form.reset();
    } catch (err) {
      setFormStatus('error');
      setFormError('Error de conexión. Por favor intenta de nuevo.');
    }
  };

  return (
    <>
      <SEOHead
        title="Contacto | Suvi Digital - Agencia de Marketing para Agencias de Viaje"
        description="Contacta con Suvi Digital para marketing digital, branding y e-commerce para tu agencia de viaje. Asesoría gratuita disponible."
        keywords="contacto agencia marketing digital, asesoria marketing turismo, Suvi Digital contacto, marketing agencias viaje Peru"
        canonical="https://suvidigital.com/contacto"
        schemaType="ContactPage"
      />
      <main className="min-h-screen">
        {/* Hero */}
        <section className="relative min-h-[420px] md:min-h-[500px] flex items-center overflow-hidden">
          <div className="absolute inset-0 animate-gradient bg-gradient-to-br from-primary-100 via-background-50 to-secondary-100" />
          <div className="absolute top-20 right-20 w-72 h-72 bg-primary-200/30 rounded-full blur-3xl animate-float-slow" />
          <div className="absolute bottom-20 left-10 w-64 h-64 bg-accent-200/20 rounded-full blur-3xl animate-float-delay-1" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: `radial-gradient(circle, oklch(var(--foreground-900)) 1px, transparent 1px)`, backgroundSize: '40px 40px' }}
          />

          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 xl:px-16 pt-24 md:pt-28">
            <div className="max-w-4xl">
              <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-5 border border-primary-200/50">
                Hablemos
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-900 mb-5 leading-tight">
                Agenda tu{' '}
                <span className="text-gradient">asesoría gratuita</span>
              </h1>
              <p className="text-foreground-500 text-lg md:text-xl leading-relaxed max-w-2xl">
                Completa el formulario y nos pondremos en contacto contigo en menos de 24 horas. Sin compromisos.
              </p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-white py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16">
            <div className="grid lg:grid-cols-5 gap-10 lg:gap-16">
              {/* Form */}
              <div className="lg:col-span-3">
                <div className="bg-background-50 border border-background-200/60 rounded-2xl p-6 md:p-10">
                  {formStatus === 'success' ? (
                    <div className="text-center py-10">
                      <div className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-5">
                        <i className="ri-check-line text-2xl" />
                      </div>
                      <h3 className="font-heading font-bold text-2xl text-foreground-900 mb-3">¡Mensaje enviado!</h3>
                      <p className="text-foreground-500 leading-relaxed mb-6">
                        Gracias por contactarnos. Te responderemos en menos de 24 horas.
                      </p>
                      <button
                        onClick={() => setFormStatus('idle')}
                        className="bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-3 rounded-xl transition-colors whitespace-nowrap"
                      >
                        Enviar otro mensaje
                      </button>
                    </div>
                  ) : (
                    <form ref={formRef} onSubmit={handleSubmit} data-readdy-form id="contacto-form" className="space-y-5">
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="name" className="block text-foreground-700 text-sm font-medium mb-2">Nombre completo *</label>
                          <input type="text" id="name" name="name" required className="w-full bg-white border border-background-300 rounded-xl px-4 py-3 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all" placeholder="Tu nombre" />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-foreground-700 text-sm font-medium mb-2">Email *</label>
                          <input type="email" id="email" name="email" required className="w-full bg-white border border-background-300 rounded-xl px-4 py-3 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all" placeholder="tu@email.com" />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="company" className="block text-foreground-700 text-sm font-medium mb-2">Empresa / Agencia</label>
                          <input type="text" id="company" name="company" className="w-full bg-white border border-background-300 rounded-xl px-4 py-3 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all" placeholder="Nombre de tu agencia" />
                        </div>
                        <div>
                          <label htmlFor="phone" className="block text-foreground-700 text-sm font-medium mb-2">Teléfono / WhatsApp</label>
                          <input type="tel" id="phone" name="phone" className="w-full bg-white border border-background-300 rounded-xl px-4 py-3 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all" placeholder="+51 999 999 999" />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="service" className="block text-foreground-700 text-sm font-medium mb-2">Servicio de interés</label>
                        <select id="service" name="service" className="w-full bg-white border border-background-300 rounded-xl px-4 py-3 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all appearance-none">
                          <option value="">Selecciona un servicio</option>
                          <option value="estrategia">Estrategia Digital</option>
                          <option value="branding">Branding e Identidad Visual</option>
                          <option value="ecommerce">E-commerce</option>
                          <option value="redes">Redes Sociales</option>
                          <option value="publicidad">Publicidad Digital</option>
                          <option value="seo">SEO y Analítica</option>
                          <option value="operturi">Software Operturi</option>
                          <option value="otro">Otro / No estoy seguro</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-foreground-700 text-sm font-medium mb-2">Mensaje *</label>
                        <textarea id="message" name="message" required rows={5} maxLength={500} className="w-full bg-white border border-background-300 rounded-xl px-4 py-3 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all resize-none" placeholder="Cuéntanos sobre tu agencia y qué necesitas..." />
                        <p className="text-foreground-400 text-xs mt-1">Máximo 500 caracteres</p>
                      </div>
                      {/* Honeypot */}
                      <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />

                      {formError && (
                        <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl px-4 py-3">
                          {formError}
                        </div>
                      )}

                      <button type="submit" disabled={formStatus === 'submitting'} className="w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-4 rounded-xl transition-all whitespace-nowrap text-base disabled:opacity-60 disabled:cursor-not-allowed btn-glow">
                        {formStatus === 'submitting' ? (
                          <span className="flex items-center justify-center gap-2">
                            <i className="ri-loader-4-line animate-spin" /> Enviando...
                          </span>
                        ) : 'Enviar Mensaje'}
                      </button>
                    </form>
                  )}
                </div>
              </div>

              {/* Info */}
              <div className="lg:col-span-2">
                <div className="space-y-8">
                  <div className="bg-gradient-to-br from-primary-50 to-secondary-50 border border-primary-200/40 rounded-2xl p-6">
                    <h3 className="font-heading font-bold text-xl text-foreground-900 mb-5">Información de contacto</h3>
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white text-primary-500 flex-shrink-0">
                          <i className="ri-mail-line text-lg" />
                        </div>
                        <div>
                          <p className="text-foreground-800 text-sm font-medium">Email</p>
                          <a href="mailto:hernan@suvidigital.com" className="text-primary-600 hover:text-primary-700 text-sm transition-colors">hernan@suvidigital.com</a>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white text-primary-500 flex-shrink-0">
                          <i className="ri-phone-line text-lg" />
                        </div>
                        <div>
                          <p className="text-foreground-800 text-sm font-medium">Teléfono / WhatsApp</p>
                          <a href="https://wa.me/51989307864" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 text-sm transition-colors">+51 989 307 864</a>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white text-primary-500 flex-shrink-0">
                          <i className="ri-map-pin-line text-lg" />
                        </div>
                        <div>
                          <p className="text-foreground-800 text-sm font-medium">Ubicación</p>
                          <p className="text-foreground-500 text-sm">Lima, Perú (Servicio remoto a toda Latinoamérica y España)</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white text-primary-500 flex-shrink-0">
                          <i className="ri-time-line text-lg" />
                        </div>
                        <div>
                          <p className="text-foreground-800 text-sm font-medium">Horario de atención</p>
                          <p className="text-foreground-500 text-sm">Lunes a Viernes: 9:00 - 18:00 (PET)</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-accent-50 to-primary-50 border border-accent-200/40 rounded-2xl p-6">
                    <h4 className="font-heading font-bold text-lg text-foreground-900 mb-3">¿Prefieres WhatsApp?</h4>
                    <p className="text-foreground-500 text-sm leading-relaxed mb-4">Responde más rápido por WhatsApp. Escríbenos y te atenderemos de inmediato.</p>
                    <a href="https://wa.me/51989307864" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-white font-semibold px-6 py-3 rounded-xl transition-colors whitespace-nowrap text-sm">
                      <i className="ri-whatsapp-line" /> Escribir por WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}